import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import type { World as SceneWorld } from "./scene";
import type { PhysicsHandle } from "./physics";
import {
  createBoneCapsules,
  findBoneByName,
  findSkinnedMesh,
  type CatBoneCapsules,
} from "./cat-physics";
import {
  BEHAVIORS,
  type Behavior,
  type CatKeyInput,
  type CatPose,
  POSE_TRANSITIONS,
  SCALE,
  TURN_CLIPS,
} from "./cat-config";
import { headingTowardCenter, pickHeading, pickWeighted } from "./cat-utils";
import {
  type AIContext,
  createCatAnimator,
  type NextBehaviorDecision,
} from "./cat-animator";

export type { CatPose, CatKeyInput };

// Loads `public/models/cat.glb` (Indie Cat pack — see
// scripts/build/swap-cat-model.mjs) and wires up the AI loop.
//
// The animation plumbing — mixer, AnimationActions, mode state machine,
// post-turn ramp, controlled-mode dispatcher, per-frame steppers — lives
// in cat-animator.ts. This file owns the top-level orchestration: GLTF
// load, action pool registration, and the AI policy callbacks
// (`pickNext` / `pickRedirect`) that decide what to play when a behavior
// expires or the cat hits a wall. The animator invokes those when it
// needs a decision.
//
// To add a new clip: include it in `KEEP_CLIPS` (in
// scripts/build/swap-cat-model.mjs), regenerate the GLB, then add a row
// to `BEHAVIORS` (in src/cat-config.ts) with the right `pose` field —
// the animator's state machine handles the rest.

export interface CatHandle {
  group: THREE.Group;
  setVisible: (visible: boolean) => void;
  availableBehaviors: () => string[];
  /** Behaviors grouped by pose. Excludes turn clips and pose-transition
   * clips — only the looping/one-shot behaviors that drive the cat
   * once it's settled in a given pose. Useful for HUDs and tests that
   * want to enumerate "what can the cat do right now per pose." */
  behaviorsByPose: () => Record<CatPose, string[]>;
  /** The pose the cat is currently in. Updates as pose transitions
   * complete. */
  currentPose: () => CatPose;
  /** The clip name currently driving the cat's animation. May be a
   * pose-transition or turn clip when one's mid-flight. Useful for
   * tests and diagnostics. */
  currentClip: () => string;
  /** Transition the cat to the given pose, settling into the pose's
   * idle clip. No-op if already there. */
  transitionToPose: (pose: CatPose) => void;
  triggerBehavior: (clip: string) => void;
  /** Debug / inspection sub-handle. Production code (cat-ai.ts,
   * interaction.ts, main.ts wiring) does NOT touch these; only the
   * debug HUD and the test harness in `scripts/tests/` do. Behind a
   * sub-handle so a stray runtime caller can't accidentally reach for
   * one of them. */
  debug: CatDebugHandle;
  /**
   * Send the cat to a world-space point. The cat steers + locomotes
   * toward the target until it arrives, then resumes its autonomous AI.
   * Pass `null` to cancel a waypoint without arriving. The Y component
   * is ignored — the cat walks on the floor.
   */
  setWaypoint: (target: THREE.Vector3 | null) => void;
  /**
   * Snapshot of WASD-style keys held this frame. While any key is held,
   * the cat is fully player-controlled and the autonomous AI sleeps.
   * `forward` plays Walk_F; `left`/`right` rotate the cat in place;
   * `backward` is treated as "stop" (we don't have a rear-walk clip).
   * Releasing all keys with no waypoint hands control back to the AI.
   */
  setKeyInput: (input: CatKeyInput) => void;
}

export interface CatDebugHandle {
  /** Pause / resume the autonomous behavior picker. While disabled,
   * the cat still responds to `triggerBehavior`, `transitionToPose`,
   * and player control (waypoint / WASD) — only the `pickWeighted`
   * call after a behavior's duration expires is suppressed. Used by
   * the debug picker so manual pose triggers don't get clobbered by
   * the AI choosing a different behavior right after the transition
   * settles. Defaults to enabled. */
  setAIEnabled: (enabled: boolean) => void;
  /** True when the cat is mid-transition (pose-transition clip queue
   * playing) or mid-turn (Turn-45/90 clip playing). Debug UIs poll
   * this to grey out their pose / clip buttons during in-flight
   * animations — clicking a new trigger mid-transition would
   * cancelInflight + start fresh, which is rarely what the user
   * wants while watching one play out. */
  isBusy: () => boolean;
  /** Per-frame `dt` multiplier applied to mixer.update and all
   * derived timers. 0 freezes the cat at its current pose; 1 is
   * normal speed; > 1 fast-forwards. Useful in debug to slow-mo a
   * transition and see exactly where artifacts appear. Affects only
   * the cat — physics, scene, and other systems use the unscaled dt
   * from world.onUpdate. */
  setAnimSpeed: (multiplier: number) => void;
  /** Progress through the currently-playing transition / turn clip,
   * in [0, 1], or null when none is active. Tracks the CURRENT clip
   * only — chained transitions (e.g. Sit_from → Lie_to) reset to 0
   * when the queue advances. Used by the debug HUD's progress bar.
   * Returns null when `isBusy()` is false. */
  transitionProgress: () => number | null;
  /** Render the cat's skeleton at time `t` (seconds, clamped to clip
   * duration) of `clip`, then return world-space positions of the
   * requested bones. Disrupts the AI's current state — caller must
   * `triggerBehavior(...)` afterwards to restore. Returns null if the
   * clip is unknown. Bones not on the skeleton are skipped silently. */
  samplePose: (
    clip: string,
    t: number,
    bones: string[],
  ) => Record<string, [number, number, number]> | null;
  /** Duration of the named clip in seconds, or null if unknown. Used by
   * the boundary-check harness to convert "first frame / last frame"
   * into concrete `t` values for `samplePose`. */
  getClipDuration: (clip: string) => number | null;
}

export async function loadCat(
  world: SceneWorld,
  physics: PhysicsHandle,
): Promise<CatHandle | null> {
  const loader = new GLTFLoader();
  const gltf = await loader.loadAsync("/models/cat.glb").catch((err) => {
    console.warn("[cupilot] cat model failed to load", err);
    return null;
  });
  if (!gltf) return null;

  const model = gltf.scene;
  model.scale.setScalar(SCALE);
  model.traverse((obj) => {
    if ((obj as THREE.Mesh).isMesh) {
      const m = obj as THREE.Mesh;
      m.castShadow = true;
      m.receiveShadow = true;
    }
  });
  world.scene.add(model);

  // Reset to identity so the bone-capsule teleport at startup uses a
  // known-good world transform. The animator's spawn-position setup
  // sets the actual position right after.
  model.position.set(0, 0, 0);
  model.rotation.set(0, 0, 0);
  model.updateMatrixWorld(true);

  const mixer = new THREE.AnimationMixer(model);

  // Pre-bake an AnimationAction per known clip. Missing clips drop out
  // with a one-time warning; their behaviors get filtered from the pool.
  const animations = gltf.animations;
  const actions = new Map<string, THREE.AnimationAction>();
  function registerAction(clipName: string, oneShot: boolean): boolean {
    const clip = animations.find((c) => c.name === clipName);
    if (!clip) return false;
    const a = mixer.clipAction(clip);
    if (oneShot) {
      a.setLoop(THREE.LoopOnce, 1);
      a.clampWhenFinished = true;
    }
    actions.set(clipName, a);
    return true;
  }

  const pool: Behavior[] = [];
  for (const b of BEHAVIORS) {
    if (registerAction(b.clip, !b.loop)) {
      pool.push(b);
    } else {
      console.warn(`[cupilot] cat clip missing: ${b.clip}`);
    }
  }
  for (const name of TURN_CLIPS) {
    if (!registerAction(name, true)) {
      console.warn(`[cupilot] cat turn clip missing: ${name}`);
    }
  }
  for (const clipName of Object.values(POSE_TRANSITIONS)) {
    if (clipName && !registerAction(clipName, true)) {
      console.warn(`[cupilot] cat pose transition clip missing: ${clipName}`);
    }
  }

  if (pool.length === 0) {
    console.warn("[cupilot] cat has no usable clips; staying static");
    return staticFallback(model);
  }

  // Per-bone kinematic capsule colliders so the cat's physics envelope
  // tracks the visible mesh through every animation. See cat-physics.ts
  // for sizing rules and the GLTFLoader name-mangling gotcha.
  const capsules: CatBoneCapsules = createBoneCapsules(model, physics);
  physics.setCatBodies(capsules.bodies);

  const animator = createCatAnimator({
    model,
    mixer,
    actions,
    pool,
    capsules,
    world,
    pickNext: aiPickNext(pool),
    pickRedirect: aiPickRedirect(pool),
  });

  return {
    group: animator.model,
    setVisible: animator.setVisible,
    availableBehaviors: () => [
      ...pool.map((b) => b.clip),
      ...TURN_CLIPS.filter((c) => actions.has(c)),
      ...Object.values(POSE_TRANSITIONS).filter(
        (c): c is string => !!c && actions.has(c),
      ),
    ],
    behaviorsByPose: () => {
      const groups: Record<CatPose, string[]> = {
        stand: [],
        sit: [],
        lie: [],
        sleep: [],
      };
      for (const b of pool) groups[b.pose].push(b.clip);
      return groups;
    },
    currentPose: animator.currentPose,
    currentClip: animator.currentClipName,
    transitionToPose: animator.transitionToPose,
    triggerBehavior: animator.triggerBehaviorByClip,
    setWaypoint: animator.setWaypoint,
    setKeyInput: animator.setKeyInput,
    debug: {
      setAIEnabled: animator.setAIEnabled,
      isBusy: animator.isBusy,
      setAnimSpeed: animator.setAnimSpeed,
      transitionProgress: animator.transitionProgress,
      samplePose: (clipName, t, boneNames) =>
        samplePose(model, mixer, animations, actions, clipName, t, boneNames),
      getClipDuration: (clipName) =>
        animations.find((c) => c.name === clipName)?.duration ?? null,
    },
  };
}

// ─── AI policy callbacks ───────────────────────────────────────────────
//
// Both callbacks are pure functions of the policy + the current AIContext.
// The animator invokes them when a decision is needed; this is where the
// behavior policy lives (pickWeighted / pickHeading / headingTowardCenter
// integration) without the animator needing to know how those work.

function aiPickNext(
  pool: Behavior[],
): (ctx: AIContext) => NextBehaviorDecision {
  return (ctx) => {
    const next = pickWeighted(pool, ctx.currentPose, ctx.current);
    if (next.pose !== ctx.currentPose) {
      return { kind: "pose", toPose: next.pose, behavior: next };
    }
    const target = next.speed !== 0 ? pickHeading(ctx.position) : undefined;
    return { kind: "behavior", behavior: next, targetHeading: target };
  };
}

function aiPickRedirect(
  pool: Behavior[],
): (ctx: AIContext) => NextBehaviorDecision {
  return (ctx) => {
    const target = headingTowardCenter(ctx.position, 0.4);
    const next = pickWeighted(pool, ctx.currentPose, ctx.current);
    if (next.pose !== ctx.currentPose) {
      return { kind: "pose", toPose: next.pose, behavior: next };
    }
    if (next.speed !== 0) {
      return {
        kind: "behavior",
        behavior: next,
        targetHeading: target,
      };
    }
    return { kind: "turn", targetHeading: target, behavior: next };
  };
}

// ─── Debug helpers ─────────────────────────────────────────────────────

function samplePose(
  model: THREE.Group,
  mixer: THREE.AnimationMixer,
  animations: THREE.AnimationClip[],
  actions: Map<string, THREE.AnimationAction>,
  clipName: string,
  t: number,
  boneNames: string[],
): Record<string, [number, number, number]> | null {
  const clip = animations.find((c) => c.name === clipName);
  const a = actions.get(clipName);
  if (!clip || !a) return null;
  // Silence every other action so only the sampled clip drives the
  // skeleton this update.
  for (const other of actions.values()) {
    if (other === a) continue;
    other.stop();
  }
  a.reset();
  a.enabled = true;
  a.setEffectiveWeight(1);
  a.time = Math.max(0, Math.min(clip.duration, t));
  a.play();
  mixer.update(0);
  model.updateMatrixWorld(true);
  const skinned = findSkinnedMesh(model);
  if (!skinned) return null;
  const result: Record<string, [number, number, number]> = {};
  const tmp = new THREE.Vector3();
  // Sample in MODEL-LOCAL space so the cat's world position / heading
  // (which the AI drifts during the harness's sampling loop) cancels
  // out — same pose at any (x, y, z, yaw) → same numbers.
  const invModel = new THREE.Matrix4().copy(model.matrixWorld).invert();
  for (const name of boneNames) {
    const b = findBoneByName(skinned, name);
    if (!b) continue;
    b.getWorldPosition(tmp);
    tmp.applyMatrix4(invModel);
    result[name] = [tmp.x, tmp.y, tmp.z];
  }
  return result;
}

function staticFallback(model: THREE.Group): CatHandle {
  return {
    group: model,
    setVisible: (v) => {
      model.visible = v;
    },
    availableBehaviors: () => [],
    behaviorsByPose: () => ({ stand: [], sit: [], lie: [], sleep: [] }),
    currentPose: () => "stand",
    currentClip: () => "",
    transitionToPose: () => {},
    triggerBehavior: () => {},
    setWaypoint: () => {},
    setKeyInput: () => {},
    debug: {
      setAIEnabled: () => {},
      isBusy: () => false,
      setAnimSpeed: () => {},
      transitionProgress: () => null,
      samplePose: () => null,
      getClipDuration: () => null,
    },
  };
}
